import React, { FC, useCallback, useEffect } from 'react';
import { Control, FieldError, FieldValues, useForm } from 'react-hook-form';
import { Box, createFilterOptions, FormControlLabel, MenuItem, Radio, RadioGroup, Typography } from '@mui/material';
import { CoreModal, CoreModalProps } from '../../../_core/components/_ui/core-modal';
import { CoreButton } from '../../../_core/components/_ui/core-button';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { CreateDistrictDto, DistrictDto } from '@families-link/shared';
import { useTranslation } from 'react-i18next';
import { CoreTextField } from '../../../_core/components/_ui/core-textfield';
import { inputBoxStyles, errorTextStyles } from './create-form.styles';
import { buttonModalWindowStyles, mainTitleModalWindowStyles } from '../../../_core/styles';
import { mergeSx } from 'merge-sx';
import { CoreAutoComplete, AutocompleteTextField } from '../../../_core/components/_ui/core-autocomplete';
import { PhoneInput } from '../../../_core/components/_ui/phone-input';

export type CreateDistrictFormModalProps = CoreModalProps & {
	defaultValues: DistrictDto;
	formError?: any;
	districtList?: Array<{ _id: string; company: string }>;
	submitAction(values: CreateDistrictDto | DistrictDto): Promise<void>;
	closeCreateModal(): void;
	isCreate: boolean;
};

export const CreateDistrictFormModal: FC<CreateDistrictFormModalProps> = (props) => {
	const [t] = useTranslation();

	const { submitAction, formError, districtList, defaultValues, closeCreateModal, isCreate, ...modalProps } = props;
	const resolver = classValidatorResolver(CreateDistrictDto);
	//const defaultValues = { name: '', linkType: 'FamilyLink', districtLinguistLinkId: '' };

	const {
		handleSubmit,
		getValues,
		setValue,
		watch,
		register,
		control,
		formState: { errors, isDirty, isLoading, isSubmitting, isSubmitted, isValid },
	} = useForm({
		defaultValues: {
			...defaultValues,
			linkType: defaultValues.districtLinguistLinkId ? 'LinguistLink' : 'FamilyLink',
		},
		resolver,
		mode: 'all',
	});

	const disableSubmit = !isValid && (isDirty || isSubmitted);
	const loadingSubmit = isLoading || isSubmitting;

	const handleFormSubmit = useCallback(
		async (values: CreateDistrictDto) => {
			if (values.linkType === 'FamilyLink') {
				values = { ...values, districtLinguistLinkId: null };
			}

			const cleanValues: CreateDistrictDto = {
				...values,
				name: values.name?.trim(),
				districtLinguistLinkId: values?.districtLinguistLinkId ?? values?.districtLinguistLinkId?._id ?? null,
			};
			await submitAction(cleanValues);
		},
		[submitAction]
	);

	const selectedId = watch('districtLinguistLinkId');
	const linkType = watch('linkType');

	const selectedDistrict = districtList?.find((d) => d._id === selectedId) ?? null;
	const filterOptions = createFilterOptions({
		stringify: (option: any) => `${option._id ?? ''} ${option.company ?? ''}`.toLowerCase(),
	});

	return (
		<CoreModal {...modalProps} bodySx={{ pt: 0 }} paperSx={{ width: '100%', maxWidth: '550px' }}>
			<Box>
				<Typography variant="h5" sx={mergeSx(mainTitleModalWindowStyles, { mb: 5 })}>
					{isCreate ? t('districts.forms.create-districts') : t('districts.forms.edit-districts')}
				</Typography>
				<Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
						<Box>
							<CoreTextField
								{...register('name')}
								label={t('districts.forms.labels.name')}
								requiredMark
								fullWidth
								placeholder={t('districts.forms.placeholders.name') ?? ''}
								error={!!errors['name']}
								helperText={errors['name'] && errors['name']?.message}
							/>
						</Box>
						<Box>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
								<Typography sx={{ fontWeight: 400, fontSize: '14px', color: (theme) => theme.colors.gray }}>
									{t('districts.forms.labels.linkType')}
								</Typography>
								<Typography component="span" sx={{ color: 'error.main' }}>
									*
								</Typography>
							</Box>
							<RadioGroup row onChange={(e) => setValue('linkType', e.target.value)} value={linkType} sx={{ gap: 3 }}>
								<FormControlLabel
									value="FamilyLink"
									control={<Radio />}
									label={t('districts.forms.labels.familyLink')}
									//	onClick={() => setValue('districtLinguistLinkId', null)}
								/>
								<FormControlLabel
									value="LinguistLink"
									control={<Radio />}
									label={t('districts.forms.labels.linguistLink')}
								/>
							</RadioGroup>
						</Box>

						<Box>
							<CoreAutoComplete
								name="districtLinguistLinkId"
								label={t('districts.columns.account-id')}
								disabled={linkType === 'FamilyLink'}
								requiredMark
								control={control as unknown as Control<FieldValues>}
								controlSx={{ width: '100%' }}
								options={districtList || []}
								multiple={false}
								value={selectedDistrict}
								onChange={(_, selected) => {
									setValue('districtLinguistLinkId', selected?._id ?? '');
								}}
								filterOptions={filterOptions}
								getOptionLabel={(option) => option?._id ?? option}
								isOptionEqualToValue={(option, value) =>
									typeof value === 'string' ? option._id === value : option._id === value?._id
								}
								renderOption={(props, option) => (
									<MenuItem {...props} key={option._id}>
										<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
											<Typography sx={{ fontSize: '14px' }}>{option._id}</Typography>
											{option.company?.trim() && (
												<Typography sx={{ fontSize: '14px', color: '#9397A4' }}>({option.company})</Typography>
											)}
										</Box>
									</MenuItem>
								)}
								renderInput={(params) => (
									<AutocompleteTextField
										{...params}
										placeholder={!getValues('districtLinguistLinkId') ? t('common.filter-district') : ''}
										sx={{
											'& .MuiOutlinedInput-root .MuiAutocomplete-input': {
												padding: '4.5px 4px 5.5px 5px',
												minWidth: 0,
											},
											'& .MuiInputBase-input': {
												height: '100%',
												padding: '0',
											},
											'& .MuiOutlinedInput-root': {
												'&.Mui-focused fieldset': {
													borderColor: (theme) => theme.colors.blue,
												},
											},
										}}
									/>
								)}
							/>
						</Box>
						<Box>
							<PhoneInput
								fullWidth
								id="contactNumber"
								label={t('districts.forms.labels.contact-number')}
								control={control}
								name="contactNumber"
								error={!!errors['contactNumber']}
								helperText={(errors['contactNumber'] as FieldError)?.message}
							/>
						</Box>
					</Box>
					{formError && (
						<Typography sx={errorTextStyles}>
							{formError?.data?.message ? formError.data.message : t('errors.invalid-credentials-error')}
						</Typography>
					)}
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'flex-end',
							alignItems: 'center',
							gap: 3,
							mt: 5,
						}}
					>
						<CoreButton onClick={closeCreateModal} variant="secondary" sx={buttonModalWindowStyles}>
							{t('districts.forms.buttons.close-form')}
						</CoreButton>
						<CoreButton
							type="submit"
							contraModeStyleButton
							disabled={disableSubmit}
							loading={loadingSubmit}
							sx={buttonModalWindowStyles}
						>
							{isCreate ? t('districts.forms.buttons.create-district') : t('districts.forms.buttons.save')}
						</CoreButton>
					</Box>
				</Box>
			</Box>
		</CoreModal>
	);
};
